/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { Animated, Dimensions, ScrollView, View } from "react-native";
import { ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-react";
import Rodal from "rodal";
import "rodal/lib/rodal.css";
import parse from "html-react-parser";
import Icon from "react-icons-kit";
import { videoCamera } from "react-icons-kit/fa/videoCamera";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import {
  Button,
  BackButton,
  FadeInOutView,
  LoadingDots,
  Text,
} from "UpduoLibrary";
import {
  skaleBlack,
  skaleWhite,
  skaleCircleGray,
  upduoPrimaryBlue,
  upduoLighterGray,
  colorWithOpacity,
} from "config/UpduoColors";
import { SkillType } from "graphql/public/generated_public";
import SessionInfoWidget from "./SessionInfoWidget";
import SessionPartnerStatus from "./SessionPartnerStatus";

interface AgendaModalProps {
  buttonText: string;
  buttonDisabled: boolean;
  onButtonPress: () => void;
  onClose: () => void;
  cancelAlertVisible: boolean;
  communityName: string;
  isRecording: boolean;
  audioTrack: IMicrophoneAudioTrack | null;
  videoTrack: ICameraVideoTrack | null;
}

export default function AgendaModal({
  buttonText,
  buttonDisabled,
  onButtonPress,
  onClose,
  cancelAlertVisible,
  communityName,
  isRecording,
}: AgendaModalProps) {
  const skillObject = useSelector(
    (state: { skillObject: SkillType }) => state.skillObject
  );
  const skillDescriptionCleaned = skillObject.description
    .replace(/\r\n/g, "")
    .replace(/{SKILL_NAME}/g, skillObject.name)
    .replace(/{COMMUNITY_NAME}/g, communityName);

  const isSelfChatReady = useSelector(
    (state: RootState) => state.isSelfChatReady
  );
  const isPartnerChatReady = useSelector(
    (state: RootState) => state.isPartnerChatReady
  );
  const partnerObject = useSelector((state: RootState) => state.partnerObject);
  const chatAgendaModalVisible = useSelector(
    (state: RootState) => state.chatAgendaModalVisible
  );

  // window size
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );

  const [windowHeight, setWindowHeight] = useState(
    Dimensions.get("window").height
  );

  const _handleWindowResize = () => {
    setWindowWidth(Dimensions.get("window").width);
    setWindowHeight(Dimensions.get("window").height);
  };

  useEffect(() => {
    window.addEventListener("resize", _handleWindowResize);
    return () => {
      window.removeEventListener("resize", _handleWindowResize);
    };
  }, []);

  // partner status animation
  const animation = new Animated.Value(0);

  const [onShowCountDownScreen, setOnShowCountDownScreen] = useState(false);
  const [countDownSeconds, setCountDownSeconds] = useState(5);

  useEffect(() => {
    if (isSelfChatReady && isPartnerChatReady && !onShowCountDownScreen) {
      setOnShowCountDownScreen(true);
      setCountDownSeconds(5);

      const interval = setInterval(() => {
        setCountDownSeconds((countDownSeconds) =>
          countDownSeconds > 0 ? countDownSeconds - 1 : 0
        );
      }, 1000);
      setTimeout(() => {
        clearInterval(interval);
        setOnShowCountDownScreen(false);
      }, 5000);
    }
  }, [isSelfChatReady, isPartnerChatReady, onShowCountDownScreen]);

  const countDownHeadingAnimatedStyle = {
    fontSize: animation.interpolate({
      inputRange: [0, 1, 2],
      outputRange: [10, 30, 24],
    }),
    lineHeight: 24 * 1.4,
  };

  const countDownTextAnimatedStyle = {
    fontSize: animation.interpolate({
      inputRange: [0, 1, 2],
      outputRange: [10, 25, 20],
    }),
    lineHeight: 20 * 1.4,
  };

  const countDownSecondAnimatedStyle = {
    fontSize: animation.interpolate({
      inputRange: [0, 1, 2],
      outputRange: [10, 60, 48],
    }),
    lineHeight: 48 * 1.4,
  };

  if (countDownSeconds === 5) {
    Animated.sequence([
      Animated.timing(animation, {
        toValue: 1,
        duration: 200,
        useNativeDriver: false,
      }),
      Animated.timing(animation, {
        toValue: 2,
        duration: 150,
        useNativeDriver: false,
      }),
    ]).start();
  }

  const countDownScreen = () => {
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          paddingTop: 95,
          width: windowWidth / 2.5,
        }}
      >
        <Animated.Text
          style={{
            ...countDownHeadingAnimatedStyle,
            fontFamily: "ObjectSans-Regular",
            color: upduoPrimaryBlue,
            fontWeight: "800",
            marginBottom: 10,
          }}
        >
          {"Get Ready!"}
        </Animated.Text>

        <Animated.Text
          style={{
            ...countDownTextAnimatedStyle,
            fontFamily: "ObjectSans-Regular",
            color: skaleBlack,
            fontWeight: "400",
            marginBottom: 15,
          }}
        >
          {"conversation will start in..."}
        </Animated.Text>

        <Animated.Text
          style={{
            ...countDownSecondAnimatedStyle,
            fontFamily: "ObjectSans-Regular",
            color: upduoPrimaryBlue,
            fontWeight: "800",
          }}
        >
          {countDownSeconds}
        </Animated.Text>
      </View>
    );
  };

  const buttonContent =
    buttonText !== "Ready" && !onShowCountDownScreen ? (
      <View style={{ alignItems: "center" }}>
        <LoadingDots color={skaleCircleGray} size={5} />
        <Text
          style={{
            marginTop: -5,
            fontSize: 12,
            color: skaleCircleGray,
          }}
        >
          {buttonText}
        </Text>
      </View>
    ) : (
      <View
        style={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <Text
          style={{
            color: skaleWhite,
          }}
        >
          <Icon size={20} icon={videoCamera} />
        </Text>
        <Text
          style={{
            fontSize: 16,
            fontWeight: "bold",
            textTransform: "uppercase",
            color: skaleWhite,
            paddingHorizontal: 8,
          }}
        >
          {"Ready"}
        </Text>
      </View>
    );

  return (
    <Rodal
      visible={chatAgendaModalVisible}
      customStyles={{
        backgroundColor: colorWithOpacity(skaleWhite, 0.9),
        margin: 0,
        width: windowWidth,
        height: windowHeight,
      }}
      onClose={() => {}}
    >
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          marginTop: 40,
        }}
      >
        <Text style={{ fontSize: 20, fontWeight: "500", marginBottom: "5%" }}>
          {"Match Found!"}
        </Text>
        {onShowCountDownScreen ? (
          countDownScreen()
        ) : (
          <View
            style={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <View>
              <View style={{ flexGrow: 0, width: windowWidth / 3 }}>
                <SessionInfoWidget title={"LESSON INFORMATION"}>
                  <ScrollView style={{ maxHeight: 500 }}>
                    <View style={{ flexDirection: "row" }}>
                      <Text
                        style={{
                          fontSize: 16,
                          fontWeight: "500",
                          color: skaleBlack,
                        }}
                      >
                        {`"${skillObject.name}"`}
                      </Text>
                    </View>
                    <Text style={{ fontSize: 16, color: skaleBlack }}>
                      {parse(skillDescriptionCleaned)}
                    </Text>
                  </ScrollView>
                </SessionInfoWidget>
              </View>
            </View>

            <FadeInOutView
              style={{
                margin: 50,
              }}
            >
              <View
                style={{
                  marginBottom: 10,
                  width: windowWidth / 2.5,
                }}
              >
                <SessionPartnerStatus partnerObject={partnerObject} />
              </View>
              {isRecording ? (
                <Text
                  style={{
                    fontStyle: "italic",
                    alignSelf: "center",
                    textAlign: "center",
                    marginVertical: 5,
                    paddingTop: 20,
                  }}
                >
                  {"This session may be recorded"}
                </Text>
              ) : null}
              <Button
                disabled={onShowCountDownScreen ? true : buttonDisabled}
                onPress={onButtonPress}
                text={buttonText}
                content={buttonContent}
                height={60}
                width={windowWidth / 2.5}
                disabledColor={
                  onShowCountDownScreen ? upduoLighterGray : undefined
                }
              />
            </FadeInOutView>
          </View>
        )}
        <View style={{ position: "absolute", top: 0, left: "13%" }}>
          <BackButton
            disabled={cancelAlertVisible}
            onPress={onClose}
            color={upduoPrimaryBlue}
          />
        </View>
      </View>
    </Rodal>
  );
}
